import { blockCategories } from '../categories';
import {
    BS_IMAGE_FIGURE_TYPE,
    BS_IMAGE_IMG_TYPE,
} from '../bootstrap-components/components/imageCaption/constants';

export const LANDING_PAGE_IMAGE_TYPE = 'landing-page-image';
const BLOCK_ID = 'landing-page-image';

const DROPPABLE_TYPES = new Set([
    BS_IMAGE_IMG_TYPE,
    BS_IMAGE_FIGURE_TYPE,
    'image',
    'swiper-container',
]);

const getPropertyLabel = (propertyValue, fieldOptions = []) => {
    const option = fieldOptions.find((entry) => entry.value === propertyValue);
    return option?.name ?? propertyValue;
};

const toInt = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

/** Empty / missing → null (unlimited). */
const toOptionalInt = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Landing-page image block: bind a media reference field + index range,
 * and drop an image or slider inside as the presentation template.
 */
export default function landingPageImage(
    editor,
    {
        fieldOptions = [],
        defaultProperty = null,
        defaultIndexFrom = 1,
        category = blockCategories.page,
    } = {}
) {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;
    const initialProperty =
        defaultProperty || fieldOptions[0]?.value || 'images';
    const defaultLabel = getPropertyLabel(initialProperty, fieldOptions);

    domComponents.addType(LANDING_PAGE_IMAGE_TYPE, {
        model: {
            defaults: {
                tagName: 'div',
                name: `Bilder: ${defaultLabel}`,
                droppable: (source) =>
                    DROPPABLE_TYPES.has(source?.get?.('type')),
                draggable: true,
                stylable: true,
                highlightable: true,
                attributes: {
                    class: 'sio-landing-page-image',
                    'data-siotype': LANDING_PAGE_IMAGE_TYPE,
                    'data-property': initialProperty,
                    'data-field-label': defaultLabel,
                    'data-index-from': String(defaultIndexFrom),
                },
                property: initialProperty,
                indexFrom: defaultIndexFrom,
                indexTo: null,
                traits: [
                    {
                        type: 'selectize',
                        name: 'property',
                        label: 'Feld',
                        options: fieldOptions,
                        changeProp: 1,
                        default: initialProperty,
                        placeholder: 'Feld suchen…',
                    },
                    {
                        type: 'number',
                        name: 'indexFrom',
                        label: 'von',
                        changeProp: 1,
                        default: defaultIndexFrom,
                        min: 1,
                    },
                    {
                        type: 'number',
                        name: 'indexTo',
                        label: 'bis',
                        changeProp: 1,
                        min: 1,
                        placeholder: '∞ (optional)',
                    },
                ],
                components: [],
            },

            init() {
                this.syncFromAttributes();
                this.on('change:property', this.onPropertyChange);
                this.on('change:indexFrom', this.onIndexChange);
                this.on('change:indexTo', this.onIndexChange);
                this.on('change:attributes', this.onAttributesChange);
            },

            syncFromAttributes() {
                const attrs = this.getAttributes() || {};
                const property =
                    attrs['data-property'] ||
                    this.get('property') ||
                    initialProperty;
                const indexFrom = toInt(
                    attrs['data-index-from'] ?? this.get('indexFrom'),
                    defaultIndexFrom
                );
                const indexTo = toOptionalInt(
                    attrs['data-index-to'] ?? this.get('indexTo')
                );

                this.set('property', property, { silent: true });
                this.set('indexFrom', indexFrom, { silent: true });
                this.set('indexTo', indexTo, { silent: true });
                this.applyTraits({ property, indexFrom, indexTo });
            },

            onAttributesChange() {
                const attrs = this.getAttributes() || {};
                const property = attrs['data-property'];
                const indexFrom = attrs['data-index-from'];
                const indexToAttr = attrs['data-index-to'];
                let changed = false;

                if (property && property !== this.get('property')) {
                    this.set('property', property, { silent: true });
                    changed = true;
                }
                if (
                    indexFrom != null &&
                    toInt(indexFrom, defaultIndexFrom) !== this.get('indexFrom')
                ) {
                    this.set('indexFrom', toInt(indexFrom, defaultIndexFrom), {
                        silent: true,
                    });
                    changed = true;
                }

                const nextIndexTo =
                    indexToAttr === undefined
                        ? this.get('indexTo')
                        : toOptionalInt(indexToAttr);
                if (nextIndexTo !== this.get('indexTo')) {
                    this.set('indexTo', nextIndexTo, { silent: true });
                    changed = true;
                }

                if (changed) {
                    this.applyTraits({
                        property: this.get('property'),
                        indexFrom: this.get('indexFrom'),
                        indexTo: this.get('indexTo'),
                        skipAttributes: true,
                    });
                }
            },

            onPropertyChange() {
                this.applyTraits({
                    property: this.get('property') || initialProperty,
                    indexFrom: this.get('indexFrom'),
                    indexTo: this.get('indexTo'),
                });
            },

            onIndexChange() {
                this.applyTraits({
                    property: this.get('property') || initialProperty,
                    indexFrom: this.get('indexFrom'),
                    indexTo: this.get('indexTo'),
                });
            },

            applyTraits(
                {
                    property,
                    indexFrom,
                    indexTo,
                    skipAttributes = false,
                } = {}
            ) {
                const label = getPropertyLabel(property, fieldOptions);
                const from = toInt(indexFrom, defaultIndexFrom);
                const to = toOptionalInt(indexTo);

                if (!skipAttributes) {
                    this.addAttributes({
                        'data-property': property,
                        'data-field-label': label,
                        'data-index-from': String(from),
                    });

                    if (to === null) {
                        this.removeAttributes('data-index-to');
                    } else {
                        this.addAttributes({
                            'data-index-to': String(to),
                        });
                    }
                } else {
                    this.addAttributes({ 'data-field-label': label });
                }

                this.set('name', `Bilder: ${property}`);
            },
        },

        view: {},
    });

    blockManager.add(BLOCK_ID, {
        label: 'Bilder für Landing-Page',
        category,
        media: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
            <circle cx="8.5" cy="10" r="1.5" fill="currentColor"/>
            <path d="M5 16l4-4 3 3 3-4 4 5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linejoin="round"/>
        </svg>`,
        content: {
            type: LANDING_PAGE_IMAGE_TYPE,
            property: initialProperty,
            indexFrom: defaultIndexFrom,
            indexTo: null,
            attributes: {
                class: 'sio-landing-page-image',
                'data-siotype': LANDING_PAGE_IMAGE_TYPE,
                'data-property': initialProperty,
                'data-field-label': defaultLabel,
                'data-index-from': String(defaultIndexFrom),
            },
        },
    });
}
